import { inject, Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserInfoService } from '../services/user-info.service';
import { catchError, map, tap } from 'rxjs/operators';
import { UserInfo, UserInfoHeader } from '../models/user-info.model';
import { UserStateService } from '../services/user-state.service';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private userInfoService = inject(UserInfoService);
  private userStateService = inject(UserStateService);

  canActivate(): Observable<boolean | UrlTree> {
    const token = this.authService.getAccessToken();
    if (!token) {
      this.authService.handleRedirect();
      return of(false);
    }

    return this.userInfoService.getUserInfo().pipe(
      tap((userInfo) => {
        if (userInfo?.sub) {
          this.userInfoService.userInfo.set(this.mapToHeader(userInfo));
          this.userStateService.setUserFromUserInfo(userInfo);
        }
      }),
      map((userInfo) => {
        if (userInfo?.sub) {
          return true;
        }

        this.redirectToLogin();
        return false;
      }),
      catchError(() => {
        this.redirectToLogin();
        return of(false);
      })
    );
  }

  private redirectToLogin(): void {
    this.authService.logout();
    this.userStateService.clearUserIdentity();
    this.authService.handleRedirect();
  }

  private mapToHeader(user: UserInfo): UserInfoHeader {
    const fullName = `${user.given_name || ''} ${user.family_name || ''}`.trim();
    this.userStateService.setEmployeeName(fullName);
    this.userStateService.setCompanyId(user.departmentnumber || '');
    return {
      username: fullName || '',
      department: user.departmentnumber || '',
      profileImg: '',
    };
  }
}
